// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DateTime } from 'luxon';
import { z } from 'zod';

export const INTERVAL_TYPES = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  YEARLY: 'yearly',
} as const;

export type IntervalType = (typeof INTERVAL_TYPES)[keyof typeof INTERVAL_TYPES];

// Create a Zod schema from the const object
export const intervalTypeSchema = z.nativeEnum(INTERVAL_TYPES);

/**
 * Formats a date label based on the interval using DateTime
 */
export const formatDateLabel = (
  dateTime: DateTime,
  interval: string
): string => {
  // Custom month abbreviations
  const monthAbbrev = [
    'Jan',
    'Feb',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug',
    'Sept',
    'Oct',
    'Nov',
    'Dec',
  ];

  const month = monthAbbrev[dateTime.month - 1]; // DateTime months are 1-indexed
  const day = dateTime.day;
  const year = dateTime.year;

  switch (interval) {
    case 'daily':
      return `${month} ${String(day)}, ${String(year)}`;
    case 'weekly':
      return `${month} ${String(day)}, ${String(year)}`;
    case 'monthly':
      return `${month} ${String(year)}`;
    case 'quarterly': {
      // Calculate quarter using (month + 2) / 3, rounded down
      const quarter = Math.floor((dateTime.month + 2) / 3);
      return `Q${String(quarter)} ${String(year)}`;
    }
    case 'yearly':
      return String(year);
    default:
      return `${month} ${String(day)}, ${String(year)}`;
  }
};

/**
 * Truncates text to a specified length and adds ellipsis
 */
export const truncateLabel = (text: string, maxLength = 12): string => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Adds an interval to a DateTime based on the interval type
 */
export const addInterval = (
  dateTime: DateTime,
  interval: IntervalType
): DateTime => {
  switch (interval) {
    case 'daily':
      return dateTime.plus({ days: 1 });
    case 'weekly':
      return dateTime.plus({ weeks: 1 });
    case 'monthly':
      return dateTime.plus({ months: 1 });
    case 'quarterly':
      return dateTime.plus({ quarters: 1 });
    case 'yearly':
      return dateTime.plus({ years: 1 });
    default:
      return dateTime.plus({ days: 1 });
  }
};

/**
 * Subtracts an interval from a DateTime based on the interval type
 */
export const subtractInterval = (
  dateTime: DateTime,
  interval: IntervalType
): DateTime => {
  switch (interval) {
    case 'daily':
      return dateTime.minus({ days: 1 });
    case 'weekly':
      return dateTime.minus({ weeks: 1 });
    case 'monthly':
      return dateTime.minus({ months: 1 });
    case 'quarterly':
      return dateTime.minus({ quarters: 1 });
    case 'yearly':
      return dateTime.minus({ years: 1 });
    default:
      return dateTime.minus({ days: 1 });
  }
};
